import { useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useToast } from '../app/ToastProvider';
import { ApiError } from '../api/client';
import type { ExportFormat } from '../api/types';

interface ExportMenuProps {
  onExport: (format: ExportFormat) => Promise<void>;
  disabled?: boolean;
}

const FORMATS: { format: ExportFormat; label: string }[] = [
  { format: 'pdf', label: 'Export as PDF' },
  { format: 'csv', label: 'Export as CSV' },
  { format: 'xlsx', label: 'Export as Excel (XLSX)' },
];

export function ExportMenu({ onExport, disabled = false }: ExportMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const run = async (format: ExportFormat): Promise<void> => {
    setAnchorEl(null);
    setBusy(true);
    try {
      await onExport(format);
      toast.success(`Export downloaded as ${format.toUpperCase()}.`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'The export could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DownloadOutlinedIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled || busy}
      >
        {busy ? 'Exporting…' : 'Export'}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {FORMATS.map((option) => (
          <MenuItem key={option.format} onClick={() => void run(option.format)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
